import * as React from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Searchable select of library modules for the import review (T-0153).
 *
 * A button showing the current choice; opening it shows a search field
 * and the matching modules, plus "Leave these rows out" (unless
 * `allowSkip` is false, as in the AI document intake, where skipping is a
 * decision on the candidate, not a module choice). Closes on a
 * choice, on Esc and on a click elsewhere. While it is open the element
 * carries `data-import-popup-open`, which the import sheet reads so that
 * Esc closes this list and not the whole sheet.
 */

export interface ModuleOption {
  id: string;
  name: string;
}

/** `value` of the "leave these rows out" choice. */
export const SKIP_MODULE = '__skip__';

export interface ModuleSelectProps {
  /** A module id, `SKIP_MODULE`, or null while nothing is chosen. */
  value: string | null;
  modules: ReadonlyArray<ModuleOption>;
  onChange: (value: string) => void;
  'aria-label': string;
  /** Highlight as a choice the reviewer still has to make. */
  required?: boolean;
  disabled?: boolean;
  /** Offer "Leave these rows out". Default true. */
  allowSkip?: boolean;
}

export function ModuleSelect({
  value,
  modules,
  onChange,
  'aria-label': ariaLabel,
  required = false,
  disabled = false,
  allowSkip = true,
}: ModuleSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const listId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const needle = query.trim().toLowerCase();
  const shown = needle ? modules.filter((m) => m.name.toLowerCase().includes(needle)) : modules;
  const current =
    value === SKIP_MODULE
      ? 'Leave these rows out'
      : value
        ? (modules.find((m) => m.id === value)?.name ?? 'Unknown module')
        : 'Choose a module…';

  const choose = (next: string) => {
    setOpen(false);
    setQuery('');
    onChange(next);
    buttonRef.current?.focus();
  };

  const option = (id: string, label: string, muted = false) => (
    <li key={id} role="presentation">
      <button
        type="button"
        role="option"
        aria-selected={value === id}
        onClick={() => choose(id)}
        className={cn(
          'flex h-8 w-full items-center rounded-chip px-2.5 text-left text-body-sm hover:bg-paper-100 focus-visible:bg-paper-100 focus-visible:outline-none',
          value === id ? 'font-semibold text-ink-900' : muted ? 'text-ink-500' : 'text-ink-700',
        )}
      >
        <span className="truncate">{label}</span>
      </button>
    </li>
  );

  return (
    <div
      ref={rootRef}
      className="relative min-w-0"
      {...(open ? { 'data-import-popup-open': '' } : {})}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${ariaLabel}: ${current}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-[30px] w-full items-center gap-2 rounded-card border px-2.5 text-left text-body-sm',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-amber-600',
          'disabled:cursor-not-allowed disabled:opacity-50',
          required && value == null
            ? 'border-posture-critical bg-posture-critical-tint text-ink-900'
            : 'border-ink-200 bg-paper-0 text-ink-900 hover:border-ink-300',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', value == null && 'text-ink-700')}>
          {current}
        </span>
        <ChevronDown className="h-3 w-3 flex-none text-ink-500" aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-[34px] z-10 rounded-card border border-ink-100 bg-paper-0 p-1 shadow-modal">
          <label className="mb-1 flex h-8 items-center gap-2 rounded-chip border border-ink-200 px-2.5 text-ink-500 focus-within:ring-2 focus-within:ring-accent-amber-600">
            <Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            <input
              // The list was opened on purpose: typing to search is the next step.
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Focus returns to the button: keep this Enter from pressing it.
                e.preventDefault();
                if (shown.length === 1) choose(shown[0]!.id);
              }}
              aria-label="Search modules"
              placeholder="Search modules…"
              className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink-900 placeholder:text-ink-300 focus:outline-none"
            />
          </label>
          <ul
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            className="max-h-56 overflow-y-auto"
          >
            {shown.map((m) => option(m.id, m.name))}
            {shown.length === 0 ? (
              <li className="px-2.5 py-2 text-meta text-ink-500">No module matches.</li>
            ) : null}
            {allowSkip ? (
              <>
                <li role="presentation" className="my-1 h-px bg-ink-100" />
                {option(SKIP_MODULE, 'Leave these rows out', true)}
              </>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
